/**
 * Team Settings Page (Story 1.13)
 *
 * Allows owners and admins to:
 * - View current team members
 * - Invite new members (with role selection)
 * - Remove members (owner only)
 * - Change member roles (owner only)
 *
 * Plan-gated: requires Starter+ plan.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@provenleap/ui";
import { apiGet, apiPost, apiDelete, apiPatch } from "../../../lib/apiClient";
import { useAuthStore } from "../../../stores/authStore";
import { usePermission } from "../../../hooks/usePermission";
import { PLAN_LIMITS, PLAN_DISPLAY_NAMES, type Plan } from "@provenleap/types";
import { PageSpinner } from "../../../components/PageSpinner";

export const Route = createFileRoute("/_authenticated/settings/team")({
  component: TeamSettingsPage,
});

// ============================================================================
// Types
// ============================================================================

interface TeamMember {
  userId: string;
  role: "owner" | "admin" | "viewer";
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  invitedAt: string;
  acceptedAt: string | null;
}

interface Invitation {
  id: string;
  email: string;
  role: "admin" | "viewer";
  status: string;
  expiresAt: string;
  createdAt: string;
  invitedById: string;
}

// ============================================================================
// Main Component
// ============================================================================

function TeamSettingsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { isOwner } = usePermission();
  const plan: Plan = user?.plan ?? "free";
  const isPaidPlan = plan !== "free";

  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);
  const [roleChangeTarget, setRoleChangeTarget] = useState<TeamMember | null>(
    null
  );
  const [newRole, setNewRole] = useState<"admin" | "viewer">("viewer");

  // L1 fix: Clear previous timeout before setting a new one, and on unmount
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const showToast = useCallback((type: "success" | "error", message: string) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ type, message });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // ========================================================================
  // Data Fetching (hooks MUST be called before any early returns)
  // ========================================================================

  const {
    data: members,
    isLoading: membersLoading,
    error: membersError,
  } = useQuery<TeamMember[]>({
    queryKey: ["team", "members"],
    queryFn: async () => {
      const res = await apiGet("/api/team/members");
      if (!res.ok) throw new Error("Failed to load team members");
      const json = await res.json();
      return json.data;
    },
    enabled: isPaidPlan,
  });

  const {
    data: invitations,
    isLoading: invitationsLoading,
  } = useQuery<Invitation[]>({
    queryKey: ["team", "invitations"],
    queryFn: async () => {
      const res = await apiGet("/api/team/invitations");
      if (!res.ok) throw new Error("Failed to load invitations");
      const json = await res.json();
      return json.data;
    },
    enabled: isPaidPlan,
  });

  // ========================================================================
  // Mutations
  // ========================================================================

  const removeMember = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiDelete(`/api/team/members/${userId}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(
          (err as { detail?: string }).detail || "Failed to remove member"
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team", "members"] });
      showToast("success", "Member removed");
      setRemoveTarget(null);
    },
    onError: (err: Error) => {
      showToast("error", err.message);
      setRemoveTarget(null);
    },
  });

  const changeRole = useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string;
      role: "admin" | "viewer";
    }) => {
      const res = await apiPatch(`/api/team/members/${userId}/role`, { role });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(
          (err as { detail?: string }).detail || "Failed to change role"
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team", "members"] });
      showToast("success", "Role updated");
      setRoleChangeTarget(null);
    },
    onError: (err: Error) => {
      showToast("error", err.message);
      setRoleChangeTarget(null);
    },
  });

  const revokeInvitation = useMutation({
    mutationFn: async (invitationId: string) => {
      const res = await apiDelete(`/api/team/invitations/${invitationId}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(
          (err as { detail?: string }).detail || "Failed to revoke invitation"
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team", "invitations"] });
      showToast("success", "Invitation revoked");
    },
    onError: (err: Error) => {
      showToast("error", err.message);
    },
  });

  // ========================================================================
  // Computed
  // ========================================================================

  const memberCount = members?.length ?? 0;
  const pendingCount = invitations?.length ?? 0;
  // null = unlimited (paid tiers); free tier has limit = 1.
  const limit = PLAN_LIMITS[plan].teamMembers;
  const currentCount = memberCount + pendingCount;
  const canInvite = limit === null || currentCount < limit;

  // ========================================================================
  // Render
  // ========================================================================

  // Plan gate: Free users get upgrade prompt (AFTER all hooks)
  if (!isPaidPlan) {
    return (
      <div className="p-6 max-w-[800px] mx-auto">
        <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "Team" }]} />
        <Card>
          <CardContent className="p-6 text-center">
            <UsersIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-lg font-semibold mb-2">
              Team collaboration requires a paid plan
            </h2>
            <p className="text-muted-foreground mb-4">
              Upgrade to Team or above to invite team members and collaborate
              together.
            </p>
            <Link to="/settings">
              <Button variant="outline">Back to Settings</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (membersLoading) {
    return (
      <div className="p-6 max-w-[800px] mx-auto">
        <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "Team" }]} />
        <PageSpinner />
      </div>
    );
  }

  if (membersError) {
    return (
      <div className="p-6 max-w-[800px] mx-auto">
        <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "Team" }]} />
        <Card>
          <CardContent className="p-6">
            <p className="text-destructive">
              Failed to load team members. Please try again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[800px] mx-auto">
      <h1 className="text-2xl font-bold mb-6">Team</h1>

      {/* Toast */}
      {toast && (
        <div
          role="alert"
          aria-live="polite"
          className={`mb-4 p-3 rounded-md ${
            toast.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Members Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>
                {currentCount}
                {limit === null ? "" : ` / ${limit}`} seats used ({PLAN_DISPLAY_NAMES[plan]} plan)
              </CardDescription>
            </div>
            <Button
              onClick={() => setInviteDialogOpen(true)}
              disabled={!canInvite}
              data-testid="invite-btn"
            >
              Invite Member
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="divide-y">
            {members?.map((member) => (
              <div
                key={member.userId}
                className="flex items-center justify-between py-3"
                data-testid={`member-${member.userId}`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                    {(member.displayName ?? member.email)?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {member.displayName ?? member.email}
                      {member.userId === user?.id && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          (you)
                        </span>
                      )}
                    </p>
                    {member.displayName && (
                      <p className="text-xs text-muted-foreground">
                        {member.email}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs capitalize bg-muted px-2 py-0.5 rounded">
                    {member.role}
                  </span>
                  {isOwner && member.userId !== user?.id && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setRoleChangeTarget(member);
                          setNewRole(
                            member.role === "admin" ? "viewer" : "admin"
                          );
                        }}
                        data-testid={`change-role-${member.userId}`}
                      >
                        Change Role
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive border-destructive/50 hover:bg-destructive/10"
                        onClick={() => setRemoveTarget(member)}
                        data-testid={`remove-${member.userId}`}
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pending Invitations Card */}
      {!invitationsLoading && invitations && invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Invitations</CardTitle>
            <CardDescription>
              {invitations.length} pending invitation
              {invitations.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {invitations.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center justify-between py-3"
                  data-testid={`invitation-${inv.id}`}
                >
                  <div>
                    <p className="text-sm font-medium">{inv.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Invited as {inv.role} &middot; Expires{" "}
                      {new Date(inv.expiresAt).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive border-destructive/50 hover:bg-destructive/10"
                    onClick={() => revokeInvitation.mutate(inv.id)}
                    disabled={revokeInvitation.isPending}
                    data-testid={`revoke-${inv.id}`}
                  >
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <InviteDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["team", "invitations"] });
          showToast("success", "Invitation sent");
        }}
      />

      {/* Remove Member Confirmation Dialog */}
      <Dialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Remove {removeTarget?.displayName ?? removeTarget?.email} from the
              team? They will lose access immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                removeTarget && removeMember.mutate(removeTarget.userId)
              }
              disabled={removeMember.isPending}
              data-testid="confirm-remove-btn"
            >
              {removeMember.isPending ? "Removing..." : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Confirmation Dialog */}
      <Dialog
        open={!!roleChangeTarget}
        onOpenChange={(open) => !open && setRoleChangeTarget(null)}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Change{" "}
              {roleChangeTarget?.displayName ?? roleChangeTarget?.email}
              's role to <strong>{newRole}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRoleChangeTarget(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                roleChangeTarget &&
                changeRole.mutate({
                  userId: roleChangeTarget.userId,
                  role: newRole,
                })
              }
              disabled={changeRole.isPending}
              data-testid="confirm-role-btn"
            >
              {changeRole.isPending ? "Updating..." : "Change Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Invite Dialog Component
// ============================================================================

function InviteDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "viewer">("viewer");
  const [fieldError, setFieldError] = useState<string | null>(null);

  const sendInvite = useMutation({
    mutationFn: async (data: { email: string; role: "admin" | "viewer" }) => {
      const res = await apiPost("/api/team/invitations", data);
      if (!res.ok) {
        const err = await res.json();
        // Extract field-level error or general detail
        const detail =
          (err as { errors?: Array<{ message: string }> }).errors?.[0]
            ?.message ??
          (err as { detail?: string }).detail ??
          "Failed to send invitation";
        throw new Error(detail);
      }
    },
    onSuccess: () => {
      setEmail("");
      setRole("viewer");
      setFieldError(null);
      onOpenChange(false);
      onSuccess();
    },
    onError: (err: Error) => {
      setFieldError(err.message);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFieldError("Email is required");
      return;
    }

    sendInvite.mutate({ email: trimmedEmail, role });
  }

  function handleOpenChange(newOpen: boolean) {
    if (!newOpen) {
      setEmail("");
      setRole("viewer");
      setFieldError(null);
    }
    onOpenChange(newOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Send an invitation email to add a new team member.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {fieldError && (
            <div
              className="rounded-md border border-destructive bg-destructive/5 p-3 text-sm text-destructive"
              role="alert"
            >
              {fieldError}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="invite-email" className="text-sm font-medium">
              Email Address
            </label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@example.com"
              autoComplete="email"
              data-testid="invite-email-input"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="invite-role" className="text-sm font-medium">
              Role
            </label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as "admin" | "viewer")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="invite-role-select"
            >
              <option value="viewer">Viewer — Can view dashboards and reports</option>
              <option value="admin">Admin — Can manage clients, reports, and team</option>
            </select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={sendInvite.isPending}
              data-testid="send-invite-btn"
            >
              {sendInvite.isPending ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Icon
// ============================================================================

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
